/**
 * ControllerInput implementation manages controller input events (keyboard and gamepad).
 * It converts raw keyboard/gamepad inputs into semantic events using controller profiles,
 * and provides a subscription system. The implementation uses signals for reactive state
 * and sets up event listeners that can be started/stopped based on the isActive signal.
 *
 * Note: Event listeners are set up when start() is called and cleaned up when stop() is called.
 * Components should call start() when mounting and stop() when unmounting, or use the
 * provided React hook/component wrapper.
 */
import { signal } from '@preact/signals-core'
import { detectProfile, getKeyboardProfile } from '../profiles/registry'
import type { ControllerProfile } from '../profiles/types'
import type {
  ControllerInput,
  ControllerInputDeps,
  ControllerInputEvent,
  ControllerInputHandler,
  HeldInputEvent,
  HeldInputEventHandler,
  RawInputEventInfo,
  RawInputEventHandler,
} from './types'

export function makeControllerInput(deps: ControllerInputDeps = {}): ControllerInput {
  const keyboardEmulation = deps.keyboardEmulation

  const handlers = new Set<ControllerInputHandler>()
  const rawHandlers = new Set<RawInputEventHandler>()
  const heldHandlers = new Set<HeldInputEventHandler>()
  const gamepadProfiles = new Map<number, ControllerProfile>()

  const lastUsedDevice = signal<'keyboard' | 'gamepad' | null>(null)
  const lastUsedDeviceId = signal<string | null>(null)
  const lastUsedGamepadProfile = signal<ControllerProfile | null>(null)

  /**
   * These signals are used for UI hints only. Updating them synchronously inside input handlers
   * can trigger "Cycle detected" in the signals<->React bridge when a signal update causes a
   * re-render while we're still inside the input processing call stack.
   *
   * To avoid re-entrancy/cycle warnings, we defer updates to a microtask and coalesce multiple
   * updates in the same tick (last write wins).
   */
  let pendingLastUsed: { device: 'keyboard' | 'gamepad'; deviceId: string; profile?: ControllerProfile } | null = null
  let lastUsedFlushScheduled = false
  function markLastUsed(device: 'keyboard' | 'gamepad', deviceId: string, profile?: ControllerProfile): void {
    pendingLastUsed = { device, deviceId, profile }
    if (lastUsedFlushScheduled) return
    lastUsedFlushScheduled = true
    queueMicrotask(() => {
      lastUsedFlushScheduled = false
      const pending = pendingLastUsed
      pendingLastUsed = null
      if (!pending) return
      if (lastUsedDevice.value !== pending.device) {
        lastUsedDevice.value = pending.device
      }
      if (lastUsedDeviceId.value !== pending.deviceId) {
        lastUsedDeviceId.value = pending.deviceId
      }
      if (pending.device === 'gamepad' && pending.profile) {
        if (lastUsedGamepadProfile.value !== pending.profile) {
          lastUsedGamepadProfile.value = pending.profile
        }
      } else if (pending.device === 'keyboard') {
        if (lastUsedGamepadProfile.value !== null) {
          lastUsedGamepadProfile.value = null
        }
      }
    })
  }

  // ControllerInput "activation" is decoupled from focus-navigation state.
  // We keep this signal for UI hints (e.g. game-ui adapter). It becomes true on the first
  // *actually emitted* controller event (after shared-screen filtering).
  const isActive = signal<boolean>(false)

  // Mate filter for shared screen mode
  let mateFilter: string[] | null = null

  // Cleanup functions for event listeners
  let keyboardCleanup: (() => void) | null = null
  let gamepadCleanup: (() => void) | null = null

  function subscribe(handler: ControllerInputHandler): () => void {
    handlers.add(handler)
    return () => {
      handlers.delete(handler)
    }
  }

  function subscribeRaw(handler: RawInputEventHandler): () => void {
    rawHandlers.add(handler)
    return () => {
      rawHandlers.delete(handler)
    }
  }

  function subscribeHeld(handler: HeldInputEventHandler): () => void {
    heldHandlers.add(handler)
    return () => {
      heldHandlers.delete(handler)
    }
  }

  function emitHeld(event: HeldInputEvent): boolean {
    const hasActiveDialog = typeof document !== 'undefined' &&
      document.querySelector('[role="dialog"]:not([data-state="closed"])') !== null

    const shouldFilter = !hasActiveDialog && mateFilter !== null

    if (shouldFilter && mateFilter !== null && !mateFilter.includes(event.deviceId)) {
      return false
    }

    if (!isActive.value) {
      isActive.value = true
    }

    heldHandlers.forEach(handler => {
      try {
        handler(event)
      } catch (error) {
        console.error('Error in held input handler:', error)
      }
    })

    return true
  }

  function emitRaw(event: RawInputEventInfo, opts?: { bypassFilter?: boolean }): boolean {
    // Check if there's an active dialog - if so, allow all controllers to control it
    // This allows any player who opened a dialog to control it, regardless of whose turn it is
    const hasActiveDialog = typeof document !== 'undefined' &&
      document.querySelector('[role="dialog"]:not([data-state="closed"])') !== null

    const shouldFilter = !opts?.bypassFilter && !hasActiveDialog && mateFilter !== null

    if (shouldFilter && mateFilter !== null && !mateFilter.includes(event.deviceId)) {
      return false
    }

    if (!isActive.value) {
      isActive.value = true
    }

    rawHandlers.forEach(handler => {
      try {
        handler(event)
      } catch (error) {
        console.error('Error in raw input handler:', error)
      }
    })

    return true
  }

  function emit(event: ControllerInputEvent): boolean {
    const { deviceId } = event

    if (!document.hasFocus()) {
      return false
    }

    // Menu button should always work - never filter it
    // This allows any controller to toggle focus between header and content
    const isMenuEvent = event.type === 'menu'

    // Check if there's an active dialog - if so, allow all controllers to control it
    // This allows any player who opened a dialog to control it, regardless of whose turn it is
    const hasActiveDialog = typeof document !== 'undefined' &&
      document.querySelector('[role="dialog"]:not([data-state="closed"])') !== null

    // In shared screen mode, only filter events when focus is on content area
    // Allow all controllers to navigate the header and control dialogs
    const shouldFilter = !isMenuEvent && !hasActiveDialog && mateFilter !== null

    if (shouldFilter && mateFilter !== null && !mateFilter.includes(deviceId)) {
      console.log('Filtering event:', event, 'for device:', deviceId, 'mate filter:', mateFilter)
      return false
    }

    if (!isActive.value) {
      isActive.value = true
    }

    handlers.forEach(handler => {
      try {
        handler(event)
      } catch (error) {
        console.error('Error in controller input handler:', error)
      }
    })

    return true
  }

  function setMateFilter(mateIds: string[] | null): void {
    mateFilter = mateIds
  }

  function start(): void {
    if (keyboardCleanup || gamepadCleanup) {
      // Already started
      return
    }

    // Keyboard input handler
    const keyboardProfile = getKeyboardProfile()

    const handleKeyDown = (e: KeyboardEvent) => {
      // Ignore events we ourselves synthesized to trigger native keyboard-activation
      // behavior on a focused element (e.g. opening a Radix menu on Enter) - without
      // this, re-dispatching a keydown to replay that behavior would loop back into
      // this same handler and get reprocessed as another semantic input event.
      if ((e as unknown as { merelsSynthetic?: boolean }).merelsSynthetic) {
        return
      }

      // Let browser shortcuts pass through (Cmd+1, Cmd+2, Cmd+3, etc. for tab switching)
      if ((e.metaKey || e.ctrlKey) && /^[0-9]$/.test(e.key)) {
        return
      }

      // Don't handle keys when user is typing in input fields
      const target = e.target as HTMLElement
      if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable) {
        return
      }

      // Don't intercept keys while a native ARIA menu widget (e.g. a dropdown opened
      // from the header) is open - unlike our own dialogs, which rely on this app's
      // semantic navigate/select events for their in-dialog list navigation, a menu
      // widget owns its own complete keyboard interaction (arrow-key roving focus,
      // Enter to activate, Escape to close, typeahead) per the ARIA authoring
      // practices, and stopping propagation here would prevent any of that from ever
      // reaching it, breaking the widget instead of navigating it.
      if (typeof document !== 'undefined' && document.querySelector('[role="menu"]')) {
        return
      }

      const targetControllerId = keyboardEmulation?.targetControllerId.value ?? null
      if (targetControllerId) {
        // In emulation mode, translate keys into *gamepad* button pulses and let the normal
        // polling path emit events with deviceId X1/X2/X3.
        // This avoids double-emitting keyboard events ("K") while still exercising gamepad logic.
        const key = e.key

        let buttonIndex: number | null = null

        // D-pad
        if (key === 'ArrowUp') buttonIndex = 12
        else if (key === 'ArrowDown') buttonIndex = 13
        else if (key === 'ArrowLeft') buttonIndex = 14
        else if (key === 'ArrowRight') buttonIndex = 15
        // Select (Enter, Space, or A key)
        else if (key === 'Enter' || key === ' ' || key === 'Space' || key.toLowerCase() === 'a') buttonIndex = 0
        // Exit (Escape)
        else if (key === 'Escape') buttonIndex = 1
        // Configure (X)
        else if (key.toLowerCase() === 'x') buttonIndex = 2
        // Right bumper emulation (~ only). We reserve backtick (`) for app-level shortcuts.
        else if (key === ']') buttonIndex = 5
        // Menu (M key) - ignore when modifiers are pressed, matching normal keyboard behavior
        else if (key.toLowerCase() === 'm' && !e.ctrlKey && !e.metaKey && !e.altKey && !e.shiftKey) buttonIndex = 9

        if (buttonIndex !== null) {
          e.preventDefault()
          e.stopPropagation()
          keyboardEmulation?.pulseButton(targetControllerId, buttonIndex)
        }
        return
      }

      let event: ControllerInputEvent | null = null
      let rawEventName: string | null = null
      let heldControl: HeldInputEvent['control'] | null = null

      // Check navigation keys (d-pad)
      if (e.key === keyboardProfile.dpad.up) {
        e.preventDefault()
        e.stopPropagation()
        rawEventName = 'ArrowUp key'
        event = { type: 'navigate', direction: 'up', deviceId: 'K' }
        heldControl = 'up'
      } else if (e.key === keyboardProfile.dpad.down) {
        e.preventDefault()
        e.stopPropagation()
        rawEventName = 'ArrowDown key'
        event = { type: 'navigate', direction: 'down', deviceId: 'K' }
        heldControl = 'down'
      } else if (e.key === keyboardProfile.dpad.left) {
        e.preventDefault()
        e.stopPropagation()
        rawEventName = 'ArrowLeft key'
        event = { type: 'navigate', direction: 'left', deviceId: 'K' }
        heldControl = 'left'
      } else if (e.key === keyboardProfile.dpad.right) {
        e.preventDefault()
        e.stopPropagation()
        rawEventName = 'ArrowRight key'
        event = { type: 'navigate', direction: 'right', deviceId: 'K' }
        heldControl = 'right'
      }
      // Check select key (Enter or Space)
      else if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault()
        e.stopPropagation()
        rawEventName = e.key === ' ' ? 'Space key' : 'Enter key'
        event = { type: 'select', deviceId: 'K' }
        heldControl = 'fire'
      }
      // Check configure key (X key on keyboard)
      else if (e.key.toLowerCase() === keyboardProfile.buttons.configure.toLowerCase()) {
        // Don't trigger if typing in an input field
        if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) {
          return
        }
        e.preventDefault()
        e.stopPropagation()
        rawEventName = 'X key'
        event = { type: 'configure', deviceId: 'K' }
      }
      // Check right bumper key (~ on keyboard)
      // NOTE: Backtick (`) is reserved for app-level test-mate cycling.
      else if (e.key === ']') {
        // Don't trigger if typing in an input field
        if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) {
          return
        }
        e.preventDefault()
        e.stopPropagation()
        rawEventName = 'Tilde key (right bumper)'
        event = { type: 'togglePlayer', deviceId: 'K' }
      }
      // Check exit key
      else if (e.key === keyboardProfile.buttons.exit) {
        e.preventDefault()
        e.stopPropagation()
        rawEventName = 'Escape key'
        event = { type: 'exit', deviceId: 'K' }
      }
      // Check menu key
      else if (e.key.toLowerCase() === keyboardProfile.buttons.menu.toLowerCase()) {
        // Don't toggle if modifier keys are pressed
        if (e.ctrlKey || e.metaKey || e.altKey || e.shiftKey) {
          return
        }
        e.preventDefault()
        e.stopPropagation()
        rawEventName = 'M key'
        event = { type: 'menu', deviceId: 'K' }
      }

      if (event && rawEventName) {
        const rawEmitted = emitRaw({
          deviceId: 'K',
          rawEvent: rawEventName,
          timestamp: Date.now(),
        }, { bypassFilter: event.type === 'menu' })
        const semanticEmitted = emit(event)
        if (rawEmitted || semanticEmitted) {
          markLastUsed('keyboard', 'K')
        }
        // OS key-repeat re-fires keydown while a key stays held; only the
        // first keydown is a real press edge.
        if (heldControl && !e.repeat) {
          emitHeld({ deviceId: 'K', control: heldControl, pressed: true })
        }
      }
    }

    const handleKeyUp = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement
      if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable) {
        return
      }

      let heldControl: HeldInputEvent['control'] | null = null
      if (e.key === keyboardProfile.dpad.up) heldControl = 'up'
      else if (e.key === keyboardProfile.dpad.down) heldControl = 'down'
      else if (e.key === keyboardProfile.dpad.left) heldControl = 'left'
      else if (e.key === keyboardProfile.dpad.right) heldControl = 'right'
      else if (e.key === 'Enter' || e.key === ' ') heldControl = 'fire'

      if (heldControl) {
        emitHeld({ deviceId: 'K', control: heldControl, pressed: false })
      }
    }

    if (typeof window !== 'undefined') {
      window.addEventListener('keydown', handleKeyDown, true)
      window.addEventListener('keyup', handleKeyUp, true)
      keyboardCleanup = () => {
        window.removeEventListener('keydown', handleKeyDown, true)
        window.removeEventListener('keyup', handleKeyUp, true)
      }
    }

    // Gamepad input handler
    const previousButtonStates = new Map<number, boolean[]>()
    const previousAxisStates = new Map<number, number[][]>()
    const lastNavigationTime = new Map<number, number>()

    const pollGamepads = () => {
      if (typeof navigator === 'undefined' || typeof navigator.getGamepads !== 'function') return
      const gamepads = navigator.getGamepads()

      for (let i = 0; i < gamepads.length; i++) {
        const gamepad = gamepads[i]
        if (!gamepad || !gamepad.connected) continue

        const gamepadIndex = gamepad.index

        // Detect or get profile for this gamepad
        if (!gamepadProfiles.has(gamepadIndex)) {
          const profile = detectProfile(gamepad)
          gamepadProfiles.set(gamepadIndex, profile)
        }
        const profile = gamepadProfiles.get(gamepadIndex)!

        const prevButtons = previousButtonStates.get(gamepadIndex) || []
        const prevAxes = previousAxisStates.get(gamepadIndex) || [[0, 0]]

        // Get gamepad ID for raw events
        const gamepadId = `X${gamepadIndex + 1}`

        // Check button presses (only on button down, not continuous)
        // Select button
        if (gamepad.buttons[profile.buttons.select]?.pressed && !prevButtons[profile.buttons.select]) {
          const rawEmitted = emitRaw({
            deviceId: gamepadId,
            rawEvent: `${profile.name} select button press`,
            timestamp: Date.now(),
          })
          const semanticEmitted = emit({ type: 'select', deviceId: gamepadId })
          if (rawEmitted || semanticEmitted) {
            markLastUsed('gamepad', gamepadId, profile)
          }
        }

        // Exit button
        if (gamepad.buttons[profile.buttons.exit]?.pressed && !prevButtons[profile.buttons.exit]) {
          const rawEmitted = emitRaw({
            deviceId: gamepadId,
            rawEvent: `${profile.name} exit button press`,
            timestamp: Date.now(),
          })
          const semanticEmitted = emit({ type: 'exit', deviceId: gamepadId })
          if (rawEmitted || semanticEmitted) {
            markLastUsed('gamepad', gamepadId, profile)
          }
        }

        // Menu button
        if (gamepad.buttons[profile.buttons.menu]?.pressed && !prevButtons[profile.buttons.menu]) {
          const rawEmitted = emitRaw({
            deviceId: gamepadId,
            rawEvent: `${profile.name} menu button press`,
            timestamp: Date.now(),
          }, { bypassFilter: true })
          const semanticEmitted = emit({ type: 'menu', deviceId: gamepadId })
          if (rawEmitted || semanticEmitted) {
            markLastUsed('gamepad', gamepadId, profile)
          }
        }

        // Right bumper button - toggle players in Omni mode (same behavior as 'A' key)
        if (gamepad.buttons[profile.buttons.rightBumper]?.pressed && !prevButtons[profile.buttons.rightBumper]) {
          const rawEmitted = emitRaw({
            deviceId: gamepadId,
            rawEvent: `${profile.name} right bumper press`,
            timestamp: Date.now(),
          })
          const semanticEmitted = emit({ type: 'togglePlayer', deviceId: gamepadId })
          if (rawEmitted || semanticEmitted) {
            markLastUsed('gamepad', gamepadId, profile)
          }
        }

        // Configure button (X on Xbox)
        if (gamepad.buttons[profile.buttons.configure]?.pressed && !prevButtons[profile.buttons.configure]) {
          const rawEmitted = emitRaw({
            deviceId: gamepadId,
            rawEvent: `${profile.name} configure button press`,
            timestamp: Date.now(),
          })
          const semanticEmitted = emit({ type: 'configure', deviceId: gamepadId })
          if (rawEmitted || semanticEmitted) {
            markLastUsed('gamepad', gamepadId, profile)
          }
        }

        // D-pad navigation (discrete - only on button press)
        const now = Date.now()
        let navEvent: ControllerInputEvent | null = null
        let rawNavEvent: string | null = null

        // D-pad navigation (discrete - only triggers on press, not continuous)
        if (gamepad.buttons[profile.dpad.up]?.pressed && !prevButtons[profile.dpad.up]) {
          rawNavEvent = `${profile.name} D-pad Up press`
          navEvent = { type: 'navigate', direction: 'up', deviceId: gamepadId }
        } else if (gamepad.buttons[profile.dpad.down]?.pressed && !prevButtons[profile.dpad.down]) {
          rawNavEvent = `${profile.name} D-pad Down press`
          navEvent = { type: 'navigate', direction: 'down', deviceId: gamepadId }
        } else if (gamepad.buttons[profile.dpad.left]?.pressed && !prevButtons[profile.dpad.left]) {
          rawNavEvent = `${profile.name} D-pad Left press`
          navEvent = { type: 'navigate', direction: 'left', deviceId: gamepadId }
        } else if (gamepad.buttons[profile.dpad.right]?.pressed && !prevButtons[profile.dpad.right]) {
          rawNavEvent = `${profile.name} D-pad Right press`
          navEvent = { type: 'navigate', direction: 'right', deviceId: gamepadId }
        }
        // Left-stick input is normalized into repeated discrete navigation events.
        else {
          const leftStickX = gamepad.axes[profile.axes.leftStick.x] || 0
          const leftStickY = gamepad.axes[profile.axes.leftStick.y] || 0
          const prevStickX = prevAxes[0]?.[0] || 0
          const prevStickY = prevAxes[0]?.[1] || 0

          // Calculate magnitude
          const magnitude = Math.sqrt(leftStickX * leftStickX + leftStickY * leftStickY)
          const prevMagnitude = Math.sqrt(prevStickX * prevStickX + prevStickY * prevStickY)

          const isInDeadzone = magnitude < profile.deadzone
          const wasInDeadzone = prevMagnitude < profile.deadzone

          if (!isInDeadzone) {
            // Stick is outside deadzone - determine its dominant navigation direction and intensity.
            let direction: 'up' | 'down' | 'left' | 'right'
            if (Math.abs(leftStickX) > Math.abs(leftStickY)) {
              // Horizontal movement
              direction = leftStickX < 0 ? 'left' : 'right'
            } else {
              // Vertical movement
              direction = leftStickY < 0 ? 'up' : 'down'
            }

            // Calculate intensity (normalized magnitude after deadzone)
            const intensity = Math.min(1.0, (magnitude - profile.deadzone) / (1.0 - profile.deadzone))

            // Calculate dynamic cooldown based on intensity
            const baseCooldown = Math.max(50, 200 - (intensity * 150))
            const lastNavTime = lastNavigationTime.get(gamepadIndex) || 0

            // Emit continuous events based on dynamic cooldown
            if (now - lastNavTime >= baseCooldown) {
              rawNavEvent = `${profile.name} Left stick ${direction} (${Math.round(intensity * 100)}%)`
              navEvent = { type: 'navigate', direction, deviceId: gamepadId }
              lastNavigationTime.set(gamepadIndex, now)
            }
          } else if (!wasInDeadzone) {
            // Stick just returned to deadzone - clear last navigation time
            lastNavigationTime.delete(gamepadIndex)
          }
        }

        // Both D-pad presses and normalized stick input produce the same game-facing navigation event.
        if (navEvent && rawNavEvent) {
          const rawEmitted = emitRaw({
            deviceId: gamepadId,
            rawEvent: rawNavEvent,
            timestamp: now,
          })
          const semanticEmitted = emit(navEvent)
          if (rawEmitted || semanticEmitted) {
            markLastUsed('gamepad', gamepadId, profile)
          }
        }

        // Discrete held-edge events (press AND release) for d-pad + fire —
        // for continuous-simulation games. Independent of the throttled
        // navigate-event stick handling above; reuses the same prevButtons.
        const heldButtonMap: Array<[number, HeldInputEvent['control']]> = [
          [profile.dpad.up, 'up'],
          [profile.dpad.down, 'down'],
          [profile.dpad.left, 'left'],
          [profile.dpad.right, 'right'],
          [profile.buttons.select, 'fire'],
        ]
        for (const [buttonIndex, control] of heldButtonMap) {
          const pressed = gamepad.buttons[buttonIndex]?.pressed ?? false
          const wasPressed = prevButtons[buttonIndex] ?? false
          if (pressed !== wasPressed) {
            emitHeld({ deviceId: gamepadId, control, pressed })
          }
        }

        // Update previous states
        previousButtonStates.set(gamepadIndex, gamepad.buttons.map(btn => btn.pressed))
        previousAxisStates.set(gamepadIndex, [[gamepad.axes[profile.axes.leftStick.x] || 0, gamepad.axes[profile.axes.leftStick.y] || 0]])
      }
    }

    // Poll gamepads at ~60fps
    const interval = setInterval(pollGamepads, 16)

    // Handle gamepad connection/disconnection
    const handleGamepadConnected = (e: GamepadEvent) => {
      console.log('Gamepad connected:', e.gamepad.id)
      const profile = detectProfile(e.gamepad)
      gamepadProfiles.set(e.gamepad.index, profile)
      previousButtonStates.set(e.gamepad.index, [])
      previousAxisStates.set(e.gamepad.index, [[0, 0]])
    }

    const handleGamepadDisconnected = (e: GamepadEvent) => {
      console.log('Gamepad disconnected:', e.gamepad.id)
      previousButtonStates.delete(e.gamepad.index)
      previousAxisStates.delete(e.gamepad.index)
      lastNavigationTime.delete(e.gamepad.index)
      gamepadProfiles.delete(e.gamepad.index)
    }

    if (typeof window !== 'undefined') {
      window.addEventListener('gamepadconnected', handleGamepadConnected)
      window.addEventListener('gamepaddisconnected', handleGamepadDisconnected)
      gamepadCleanup = () => {
        clearInterval(interval)
        window.removeEventListener('gamepadconnected', handleGamepadConnected)
        window.removeEventListener('gamepaddisconnected', handleGamepadDisconnected)
      }
    }
  }

  function stop(): void {
    if (keyboardCleanup) {
      keyboardCleanup()
      keyboardCleanup = null
    }
    if (gamepadCleanup) {
      gamepadCleanup()
      gamepadCleanup = null
    }
  }

  // Auto-start if in browser environment
  if (typeof window !== 'undefined') {
    start()
  }

  return {
    subscribe,
    subscribeRaw,
    subscribeHeld,
    isActive,
    lastUsedDevice,
    lastUsedDeviceId,
    lastUsedGamepadProfile,
    setMateFilter,
    start,
    stop,
  }
}
